<template>
  <SoDataTableServer
    v-model:options="tableOptions"
    :headers="tableHeaders"
    :data="tableData"
    search-placeholder="Search by title"
    searchable
    data-testid="admin-newsfeed-list-table"
    @fetch="fetchData"
  >
    <template #tableActions>
      <SoButton
        type="success"
        title="Create Article"
        prepend-icon="plus"
        data-testid="create-new-article"
        class="ml-auto"
        large
        :to="{ name: RouteNames.AdminNewsfeedCreate }"
      />
    </template>
    <template #itemActions="{ item }">
      <div class="d-flex gap-1 justify-end">
        <SoButton
          title="Edit"
          type="table-action"
          data-testid="edit-article"
          :to="{
            name: RouteNames.AdminNewsfeedEdit,
            params: { articleId: item.id },
          }"
        />
        <SoButton
          v-if="item.status === 'draft'"
          title="Publish"
          type="table-action"
          data-testid="publish-article"
          :loading="publishingId === item.id"
          @click="publishArticle(item.id)"
        />
        <SoButton
          v-else
          title="Unpublish"
          type="table-action"
          data-testid="unpublish-article"
          :loading="unpublishingId === item.id"
          @click="unpublishArticle(item.id)"
        />
      </div>
    </template>
  </SoDataTableServer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { NewsfeedService } from "@/services/NewsfeedService"
  import { NewsfeedMutator } from "@/mutators/newsfeed/NewsfeedMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import {
    FilterType,
    SoDataTableColumnType,
    SoDataTableFilters,
    SoDataTableHeader,
  } from "@/types/components/SoDataTable"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { RouteNames } from "@/types/router"
  import type {
    IPaginatedNewsfeed,
    ArticleQueryParams,
  } from "@/models/newsfeed/newsfeed.types"
  import type { QueryResult } from "@/api/utils/types"

  const headers: SoDataTableHeader[] = [
    {
      title: "Title",
      key: "title",
    },
    {
      title: "Type",
      key: "type",
      type: SoDataTableColumnType.CHIP,
    },
    {
      title: "Status",
      key: "status",
      type: SoDataTableColumnType.CHIP,
    },
    {
      title: "Author",
      key: "author",
    },
    {
      title: "Publish Date",
      key: "publishDate",
      type: SoDataTableColumnType.DATE,
      fallbackValue: "Not published",
    },
    {
      title: "Updated",
      key: "updatedAt",
      type: SoDataTableColumnType.DATE,
    },
  ]

  interface NewsfeedFilters extends SoDataTableFilters {
    type: {
      type: FilterType.RADIO
      label: string
      options: Record<string, string>
      value: string | null
    }
    status: {
      type: FilterType.RADIO
      label: string
      options: Record<string, string>
      value: string | null
    }
  }

  const defaultFilters: NewsfeedFilters = {
    type: {
      type: FilterType.RADIO,
      label: "Type",
      options: {
        "": "All",
        bug: "Bug",
        incident: "Incident",
        process: "Process",
        information: "Information",
        other: "Other",
      },
      value: null,
    },
    status: {
      type: FilterType.RADIO,
      label: "Status",
      options: {
        "": "All",
        draft: "Draft",
        published: "Published",
      },
      value: null,
    },
  }

  export default defineComponent({
    components: {
      SoButton,
      SoDataTableServer,
    },
    data() {
      return {
        RouteNames,
        snackbar: useSnackbarStore(),
        tableHeaders: headers,
        tableOptions: {
          search: "",
          filters: { ...defaultFilters },
        } as Partial<SoDataTableOptions<NewsfeedFilters>>,
        query: null as QueryResult<IPaginatedNewsfeed> | null,
        publishingId: null as string | null,
        unpublishingId: null as string | null,
      }
    },
    computed: {
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.total ?? 0,
          items: this.query?.data?.items ?? [],
        }
      },
    },
    methods: {
      fetchData(options: SoDataTableOptions<NewsfeedFilters>) {
        const typeValue = options.filters?.type?.value
        const statusValue = options.filters?.status?.value
        const params: ArticleQueryParams = {
          page: options.page,
          pageSize: options.pageSize,
          search: options.search || undefined,
          type:
            typeValue && typeValue !== ""
              ? (typeValue as ArticleQueryParams["type"])
              : undefined,
          status:
            statusValue && statusValue !== ""
              ? (statusValue as ArticleQueryParams["status"])
              : undefined,
          orderBy: "createdAt",
          orderDir: "desc",
        }
        this.query = NewsfeedService.getAllArticles(params)
      },
      async publishArticle(id: string) {
        this.publishingId = id
        try {
          const publishMutator = NewsfeedMutator.publishArticle(
            () => {
              this.snackbar.addSuccess("Article published")
              this.query?.refetch()
            },
            () => {
              this.snackbar.addError("Failed to publish article")
            },
          )
          await publishMutator.mutateAsync(id)
        } finally {
          this.publishingId = null
        }
      },
      async unpublishArticle(id: string) {
        this.unpublishingId = id
        try {
          const unpublishMutator = NewsfeedMutator.unpublishArticle(
            () => {
              this.snackbar.addSuccess("Article unpublished")
              this.query?.refetch()
            },
            () => {
              this.snackbar.addError("Failed to unpublish article")
            },
          )
          await unpublishMutator.mutateAsync(id)
        } finally {
          this.unpublishingId = null
        }
      },
    },
  })
</script>
